// @flow

import _ from 'lodash';

import { decodeHtmlCode, encodeHtmlCode } from './index';
import { DEFAULT_SUGGESTION_BLOCK } from 'src/constants/instant-search-display';
import { DEFAULT_SEARCH_PANEL_BLOCK } from 'src/constants/search-page-display';
import { SORT_TYPES_LABEL, SORT_TYPES_ORDER } from 'src/constants/custom-sorting';

import {
  RECOMMENDATION_TAB,
  SORT_BY_OPTION_DISPLAY_DEFAULT,
  ITEMS_V1,
  ITEMS_V2,
  FILTER_OPTIONS_TAB
} from 'src/constants/translation';

/**
 * Detect primary locale
 *
 * @param  {array} locales
 *                 [string, primary: boolean, published: boolean}
 *                 ]
 *
 * @return {string}         primary locale
 */
export const detectPrimaryLocale = (locales: Array<Object>) => {
  let primaryLocale = 'en'; // Default locale
  locales.forEach(item => {
    const { locale, primary } = item;
    if (primary) {
      primaryLocale = locale;
    }
  });

  return primaryLocale;
};

/**
 * Build initial data for filter settings
 *
 * @param  {Object} filterSettings filter settings from API
 * @param  {Array} recommendationFields raw fields of recommendation widget
 * @return {Object} initialValue
 */
export const buildInitialTranslationValues = (
  filterSettings: Object,
  recommendationFields: Array<any>,
  locales: Array<Object>,
  primaryLocale: string
) => {
  let labelTranslations = _.get(filterSettings, 'labelTranslations', {});

  const suggestionBlocks = _.get(filterSettings, 'search.suggestionBlocks', []);
  const searchPanelBlocks = _.get(filterSettings, 'search.searchPanelBlocks', {});
  const searchBoxOnclick = _.get(filterSettings, 'search.searchBoxOnclick', {});
  const suggestionNoResult = _.get(filterSettings, 'search.suggestionNoResult', {});

  // New Label translation
  labelTranslations = _.cloneDeep(labelTranslations);

  // Init values for all tabs - get from primary language
  // If this is new store, label is not exist in filterSettings, so get labelTranslations[primaryLocale]
  // If store owner just changed primary languages, get label from
  const currentTranslations = _.get(filterSettings, `labelTranslations.${primaryLocale}`, {});

  // Ensure suggestion block labels are updated in translation values (only apply to primary locale)
  ensureSuggestionBlockLabels(currentTranslations, suggestionBlocks);

  // Ensure search panel block labels are updated in translation values (only apply to primary locale)
  ensureSearchPanelBlocksLabels(currentTranslations, searchPanelBlocks);

  // Ensure search box onclick labels are updated in translation values (only apply to primary locale)
  ensureSearchBoxOnclickLabels(currentTranslations, searchBoxOnclick);

  // Ensure suggestion no result labels are updated in translation values (only apply to primary locale)
  ensureSuggestionNoResultLabels(currentTranslations, suggestionNoResult);

  // Overwrite primary (!)
  if (primaryLocale) {
    // build initial values for recommendation in primary locale
    const initialRecommendationValues = _.chain(recommendationFields)
      .keyBy('id')
      .mapValues('widgetName')
      .value();

    labelTranslations[primaryLocale] = { ...currentTranslations, ...initialRecommendationValues };
  }

  // Avoid any locale exist on labelTranslations, but not exist on shopify locales
  let allLocales = _.uniq([
    ..._.map(locales, 'locale'), // Get from Shopify locales
    ..._.keys(labelTranslations) // Get from Shopify metafields
  ]);

  allLocales.forEach(locale => {
    // Init translation group for each locale
    const translations = _.get(labelTranslations, locale);

    // Init empty object if translation of locale not exist, avoid make form dirty when change locale
    if (_.isEmpty(translations)) {
      labelTranslations[locale] = {};
    } else {
      for (const [key, value] of Object.entries(translations)) {
        labelTranslations[locale][key] = decodeBreakLine(decodeHtmlCode(value));
      }
    }
  });

  return labelTranslations;
};

/**
 * Copy suggestion block label to translation values
 * Fields:
 *    - instantSearchSuggestionsLabel
      - instantSearchCollectionsLabel
      - instantSearchProductsLabel
      - instantSearchPagesLabe
 * Why?
 * - These fields are redefined under translatable label that allows customer to display with multiple languages
 * - Before, they are stored in suggestion block (outside the translation)
 * - After, need to store in suggestion block and translation values block at primary locale
 * We need to ensure values in these blocks are the same
 *
 * @param {Object} translationValues(ref)
 * @param {Object} suggestionBlocks
 *                  [
 *                    {"type":"suggestions", "label":"Suggestions", "status":"disabled", "number":3},
 *                    {"type":"collections", "label":"Collections", "status":"active", "number":5},
 *                    ...
 *                  ]
 *
 * @return {[type]} [description]
 */
const ensureSuggestionBlockLabels = (translationValues: Object, suggestionBlocks: Object) => {
  const {
    instantSearchSuggestionsLabel,
    instantSearchCollectionsLabel,
    instantSearchProductsLabel,
    instantSearchPagesLabel
  } = translationValues;

  const values = {
    suggestions: {
      key: 'instantSearchSuggestionsLabel',
      value: instantSearchSuggestionsLabel
    },
    collections: {
      key: 'instantSearchCollectionsLabel',
      value: instantSearchCollectionsLabel
    },
    products: {
      key: 'instantSearchProductsLabel',
      value: instantSearchProductsLabel
    },
    pages: {
      key: 'instantSearchPagesLabel',
      value: instantSearchPagesLabel
    }
  };

  // Update
  suggestionBlocks.map(block => {
    const { type, label } = block;
    if (_.isEmpty(values[type])) return null;

    const translationKey = _.get(values, `${type}.key`, '');
    const translationValue = _.get(values, `${type}.value`, '');

    if (translationValue !== label) {
      translationValues[translationKey] = label;
    }

    return label;
  });

  return translationValues;
};

/**
 * Copy search panel block label to translation values
 * Fields:
 *    - collections.label
      - pages.label
      - products.label
 * Why?
 * - These fields are redefined under translatable label that allows customer to display with multiple languages
 * - Before, they are stored in search panel block (outside the translation)
 * - After, need to store in search panel block and translation values block at primary locale
 * We need to ensure values in these blocks are the same
 *
 * @param {Object} translationValues(ref)
 * @param {Object} searchPanelBlocks
 *                  {
 *                    collections: {label: "Collections", pageSize: 25, active: true, displayImage: false},
 *                    pages: {label: "Pages", pageSize: 25, active: true, displayImage: false},
 *                    products: {label: "Products", pageSize: 25, active: true, displayImage: true}
 *                  }
 *
 * @return {[type]} [description]
 */
const ensureSearchPanelBlocksLabels = (translationValues: Object, searchPanelBlocks: Object) => {
  const {
    searchPanelCollection,
    searchPanelPage,
    searchPanelProduct,
    resultEmpty,
    searchTipsTitle,
    searchTipsContent,
    searchTermSuggestions,
    mostPopularProducts
  } = translationValues;

  const values = [
    { key: 'products.label', mappingKey: 'searchPanelProduct', value: searchPanelProduct },
    {
      key: 'pages.label',
      mappingKey: 'searchPanelPage',
      value: searchPanelPage
    },
    {
      key: 'collections.label',
      mappingKey: 'searchPanelCollection',
      value: searchPanelCollection
    },
    {
      key: 'searchEmptyResultMessages.label',
      mappingKey: 'resultEmpty',
      value: resultEmpty
    },
    {
      key: 'searchTips.label',
      mappingKey: 'searchTipsTitle',
      value: searchTipsTitle
    },
    {
      key: 'searchTips.searchTips',
      mappingKey: 'searchTipsContent',
      value: searchTipsContent
    },
    {
      key: 'searchTermSuggestions.label',
      mappingKey: 'searchTermSuggestions',
      value: searchTermSuggestions
    },
    {
      key: 'mostPopularProducts.label',
      mappingKey: 'mostPopularProducts',
      value: mostPopularProducts
    }
  ];

  // Update
  values.map(block => {
    const { key, value, mappingKey } = block;
    const label = _.get(searchPanelBlocks, key, '');
    if (label !== value) translationValues[mappingKey] = decodeBreakLine(label);

    return label;
  });

  return translationValues;
};

/**
 * Copy search box onclick label to translation values
 * Fields:
 *    - productSuggestion.label
      - recentSearch.label
      - searchTermSuggestion.label
 * Why?
 * - These fields are redefined under translatable label that allows customer to display with multiple languages
 * - Before, they are stored in search panel block (outside the translation)
 * - After, need to store in search panel block and translation values block at primary locale
 * We need to ensure values in these blocks are the same
 *
 * @param {Object} translationValues(ref)
 * @param {Object} searchBoxOnclick
 *
 * @return {[type]} [description]
 */
const ensureSearchBoxOnclickLabels = (translationValues: Object, searchBoxOnclick: Object) => {
  const {
    searchBoxOnclickProductsLabel,
    searchBoxOnclickRecentSearchLabel,
    searchBoxOnclickSearchTermLabel
  } = translationValues;

  const values = [
    {
      key: 'productSuggestion',
      mappingKey: 'searchBoxOnclickProductsLabel',
      value: searchBoxOnclickProductsLabel
    },
    {
      key: 'recentSearch',
      mappingKey: 'searchBoxOnclickRecentSearchLabel',
      value: searchBoxOnclickRecentSearchLabel
    },
    {
      key: 'searchTermSuggestion',
      mappingKey: 'searchBoxOnclickSearchTermLabel',
      value: searchBoxOnclickSearchTermLabel
    }
  ];

  // Update
  values.map(block => {
    const { key, value, mappingKey } = block;
    const label = _.get(searchBoxOnclick, `${key}.label`, '');
    if (label !== value) {
      translationValues[mappingKey] = label;
    }

    return label;
  });

  return translationValues;
};

/**
 * Copy suggestion no result label to translation values
 * Fields:
 *    - products.label
      - search_terms.label
 * Why?
 * - These fields are redefined under translatable label that allows customer to display with multiple languages
 * - Before, they are stored in search panel block (outside the translation)
 * - After, need to store in search panel block and translation values block at primary locale
 * We need to ensure values in these blocks are the same
 *
 * @param {Object} translationValues(ref)
 * @param {Object} searchBoxOnclick
 *
 * @return {[type]} [description]
 */
const ensureSuggestionNoResultLabels = (translationValues: Object, suggestionNoResult: Object) => {
  const { noSearchResultSearchTermLabel, noSearchResultProductsLabel } = translationValues;

  const values = [
    {
      key: 'products',
      mappingKey: 'noSearchResultProductsLabel',
      value: noSearchResultProductsLabel
    },
    {
      key: 'search_terms',
      mappingKey: 'noSearchResultSearchTermLabel',
      value: noSearchResultSearchTermLabel
    }
  ];

  // Update
  values.map(block => {
    const { key, value, mappingKey } = block;
    const label = _.get(suggestionNoResult, `${key}.label`, '');

    if (label !== value) {
      translationValues[mappingKey] = label;
    }

    return label;
  });
  return translationValues;
};

/**
 * encode HTML entities for text field in translation tool
 * @param {*} translationValues
 * @returns
 */
const encodeTranslationValues = (translationValues: any) => {
  const cloneValues = _.cloneDeep(translationValues);
  const locales = _.keys(cloneValues);
  locales.forEach(locale => {
    _.keys(cloneValues[locale]).forEach(key => {
      let value = cloneValues[locale][key];
      if (_.isString(value)) {
        // Field value for html display
        const isValueForHtml = [
          'searchTipsContent',
          'resultEmpty',
          'resultEmptyWithSuggestion'
        ].includes(key);
        const newValue = isValueForHtml ? encodeHtmlCode(value) : value;
        value = encodeBreakLine(newValue);
        cloneValues[locale][key] = value;
      }
    });
  });

  return cloneValues;
};

/**
 * Build translation values before saving
 *
 * @param  {object} translationValues translation values
 * @param  {object} filterSettings    filter settings
 * @param  {Array} recommendationFields raw fields of recommendation widget
 * @return {object}                   updated filter settings with translation values
 */
export const buildTranslationValues = (
  i18n: Object,
  translationValues: Object, // data from TranslationForm redux-form
  defaultTranslations: Object,
  primaryLocale: string,
  filterSettings: Object,
  dynamicFields: Object
) => {
  const { sortByOptions = [], recommendationFields = [] } = dynamicFields;

  const additionalNames = _.concat(
    _.map(sortByOptions, 'name'), // Sort by options
    _.map(recommendationFields, 'id') // Recommendation
  );

  // Fill default values for empty fields
  let filledTranslationValues = fillDefaultValues(
    translationValues,
    defaultTranslations,
    additionalNames
  );

  filledTranslationValues = encodeTranslationValues(filledTranslationValues);

  const translations = filledTranslationValues[primaryLocale] || {};

  // Update values to related features
  // Save instant search widget label to group search.suggestionBlocks
  const updatedSuggestionBlocks = buildSuggestionBlocks(translations, filterSettings, i18n);

  // Save instant search widget label to group search.searchPanelBlocks
  const updatedSearchPanelBlocks = buildSearchPanelBlocks(translations, filterSettings, i18n);

  // Save instant search widget label to group search.searchBoxOnclick
  const updatedSearchBoxOnclick = buildSearchBoxOnclick(translations, filterSettings, i18n);

  // Save instant search widget label to group search.suggestionNoResult
  const updatedSuggestionNoResult = buildSuggestionNoResult(translations, filterSettings, i18n);

  const newSettings = _.cloneDeep(filterSettings);
  // Assign primary languages to label for theme lib v2
  newSettings.label = translations;
  newSettings.labelTranslations = filledTranslationValues;

  if (!_.isEmpty(updatedSuggestionBlocks)) {
    _.set(newSettings, 'search.suggestionBlocks', updatedSuggestionBlocks);
  }

  if (!_.isEmpty(updatedSearchPanelBlocks)) {
    _.set(newSettings, 'search.searchPanelBlocks', updatedSearchPanelBlocks);
  }

  if (!_.isEmpty(updatedSearchBoxOnclick)) {
    _.set(newSettings, 'search.searchBoxOnclick', updatedSearchBoxOnclick);
  }

  if (!_.isEmpty(updatedSuggestionNoResult)) {
    _.set(newSettings, 'search.suggestionNoResult', updatedSuggestionNoResult);
  }

  return newSettings;
};

/**
 * Build suggestion blocks - Instant Search Widget
 *
 * @param  {object} translationValues translation values for primary locale
 * @param  {object} suggestionBlocks  suggestion values
 *
 * @return {object}                   updated suggestion values
 */
const buildSuggestionBlocks = (translationValues, filterSettings, i18n) => {
  // build default values
  const defaultValues = _.map(DEFAULT_SUGGESTION_BLOCK, block => {
    return { ...block, label: i18n.translate(block.label) };
  });

  // get blocks from filterSettings, get default values if not existed
  let suggestionBlocks = _.get(filterSettings, 'search.suggestionBlocks', defaultValues);

  const {
    instantSearchSuggestionsLabel = i18n.translate(DEFAULT_SUGGESTION_BLOCK[0].label),
    instantSearchCollectionsLabel = i18n.translate(DEFAULT_SUGGESTION_BLOCK[1].label),
    instantSearchProductsLabel = i18n.translate(DEFAULT_SUGGESTION_BLOCK[2].label),
    instantSearchPagesLabel = i18n.translate(DEFAULT_SUGGESTION_BLOCK[3].label)
  } = translationValues;

  // Update label for suggestion blocks
  suggestionBlocks = _.cloneDeep(suggestionBlocks).map(block => {
    const { type } = block;
    if (type === 'suggestions') block.label = instantSearchSuggestionsLabel;
    if (type === 'collections') block.label = instantSearchCollectionsLabel;
    if (type === 'products') block.label = instantSearchProductsLabel;
    if (type === 'pages') block.label = instantSearchPagesLabel;
    return block;
  });

  return suggestionBlocks;
};

/**
 * Build search panel blocks - Instant Search Widget
 *
 * @param  {object} translationValues translation values for primary locale
 * @param  {object} searchPanelBlocks  search panel values
 *
 * @return {object}                   updated search panel values
 */
const buildSearchPanelBlocks = (translationValues, filterSettings, i18n) => {
  // build default values
  let defaultValues = {};
  for (const [key, value] of Object.entries(DEFAULT_SEARCH_PANEL_BLOCK)) {
    defaultValues[key] = _.assign({}, value, {
      label: i18n.translate(value?.label, { terms: '{{ terms }}' })
    });
  }

  // get values from filterSettings, get default values if not existed
  let searchPanel = _.get(filterSettings, 'search.searchPanelBlocks', defaultValues);
  searchPanel = _.cloneDeep(searchPanel);

  const {
    searchPanelProduct = i18n.translate(DEFAULT_SEARCH_PANEL_BLOCK.products.label),
    searchPanelCollection = i18n.translate(DEFAULT_SEARCH_PANEL_BLOCK.collections.label),
    searchPanelPage = i18n.translate(DEFAULT_SEARCH_PANEL_BLOCK.pages.label),
    resultEmpty = '',
    searchTipsTitle = '',
    searchTipsContent = '',
    noSearchResultSearchTermLabel = 'Recent searches',
    noSearchResultProductsLabel = 'Trending products'
  } = translationValues;

  // Update label for suggestion blocks
  _.set(searchPanel, 'products.label', searchPanelProduct);
  _.set(searchPanel, 'collections.label', searchPanelCollection);
  _.set(searchPanel, 'pages.label', searchPanelPage);
  _.set(searchPanel, 'searchTips.label', searchTipsTitle);
  _.set(searchPanel, 'searchTips.searchTips', encodeBreakLine(searchTipsContent));
  _.set(searchPanel, 'searchEmptyResultMessages.label', encodeBreakLine(resultEmpty));
  _.set(searchPanel, 'mostPopularProducts.label', noSearchResultProductsLabel);
  _.set(searchPanel, 'searchTermSuggestions.label', noSearchResultSearchTermLabel);

  return searchPanel;
};

const encodeBreakLine = string => string.replace(/\n/g, '{{ breakline }}');
const decodeBreakLine = string => string.replace(/{{ breakline }}/g, '\n');

/**
 * Build search panel blocks - Instant Search Widget
 *
 * @param  {object} translationValues translation values for primary locale
 * @param  {object} searchBoxOnclick  search box onclick
 *
 * @return {object}                   updated search panel values
 */
const buildSearchBoxOnclick = (translationValues, filterSettings) => {
  const defaultValues = {
    productSuggestion: { label: 'Trending products', status: false, data: [] },
    recentSearch: { label: 'Recent searches', status: false, number: '3' },
    searchTermSuggestion: { label: 'Popular searches', status: false, data: [] }
  };

  let searchBoxOnclick = _.get(filterSettings, 'search.searchBoxOnclick', defaultValues);
  searchBoxOnclick = _.cloneDeep(searchBoxOnclick);

  const {
    searchBoxOnclickRecentSearchLabel = 'Recent searches',
    searchBoxOnclickSearchTermLabel = 'Popular searches',
    searchBoxOnclickProductsLabel = 'Trending products'
  } = translationValues;

  // Update label for suggestion blocks
  _.set(searchBoxOnclick, 'productSuggestion.label', searchBoxOnclickProductsLabel);
  _.set(searchBoxOnclick, 'recentSearch.label', searchBoxOnclickRecentSearchLabel);
  _.set(searchBoxOnclick, 'searchTermSuggestion.label', searchBoxOnclickSearchTermLabel);

  return searchBoxOnclick;
};

/**
 * Build suggestion no result - Instant Search Widget
 *
 * @param  {object} translationValues translation values for primary locale
 * @param  {object} suggestionNoResult  suggestion when no result
 *
 * @return {object}                   updated search panel values
 */
const buildSuggestionNoResult = (translationValues, filterSettings) => {
  const defaultValues = {
    products: { label: 'Trending products', status: false, data: [] },
    search_terms: { label: 'Popular searches', status: false, data: [] }
  };

  let suggestionNoResult = _.get(filterSettings, 'search.suggestionNoResult', defaultValues);
  suggestionNoResult = _.cloneDeep(suggestionNoResult);

  const {
    noSearchResultSearchTermLabel = 'Recent searches',
    noSearchResultProductsLabel = 'Trending products'
  } = translationValues;

  // Update label for suggestion blocks
  _.set(suggestionNoResult, 'products.label', noSearchResultProductsLabel);
  _.set(suggestionNoResult, 'search_terms.label', noSearchResultSearchTermLabel);

  return suggestionNoResult;
};

const fillDefaultValues = (
  translationValues: Object,
  defaultValues: Object,
  dynamicNames: Array<string>
) => {
  const locales = _.keys(translationValues);

  const staticFields = _(ITEMS_V1).concat(ITEMS_V2).map('name').uniq().compact().value();
  const allFields = _.concat(staticFields, dynamicNames);

  locales.forEach(locale => {
    // ignore if default values of this locale is empty
    if (_.isEmpty(defaultValues[locale])) return;
    const translationByLocale = translationValues[locale];

    const pickedDefaultValues = _.pick(defaultValues[locale], allFields);

    // get all keys of translation values
    let fields = _.keys(translationByLocale);

    // get all keys of default values
    fields = fields.concat(_.keys(pickedDefaultValues));

    // get unique fields
    fields = _.uniq(fields);

    // only update fields which type is string and value is empty
    for (const field of fields) {
      if (['amountV2'].includes(field)) continue;

      if (_.isEmpty(translationByLocale[field])) {
        translationByLocale[field] = _.get(pickedDefaultValues, field, '');
      }
    }
  });

  return translationValues;
};

/**
 * Build build recommendation fields
 *
 * @param  {object} result fields have been built
 * @param  {Array<any>} recommendationFields raw fields of recommendation widget
 */
const buildRecommendationFields = (
  result: Object,
  recommendationFields: Array<any>,
  i18n: Object
) => {
  const recFields = _.filter(recommendationFields, field => !field.id.startsWith('bundle-'));
  const bundleFields = _.filter(recommendationFields, field => field.id.startsWith('bundle-'));
  const recommendationPageTypes = _.groupBy(recFields, 'pageType');
  recommendationPageTypes['bundle'] = bundleFields;

  const recommendationDisplayFields = {};
  _.map(recommendationPageTypes, (pageTypes, key) => {
    const page = _.map(pageTypes, widget => {
      return {
        name: widget.id,
        label: `${widget.widgetName} (${i18n.translate(
          `AdminHTML.Recommendation.Common.widgetId`
        )}: ${widget.id})`,
        placeholder: widget.widgetName,
        multiline: false
      };
    });
    recommendationDisplayFields[key] = page;
  });
  result[RECOMMENDATION_TAB] = recommendationDisplayFields;
};

/**
 * Build filter option fields
 *
 * @param  {object} result fields have been built
 */
const buildFilterOptionFields = (
  result: Object,
  labelTranslations: Object,
  locale: string,
  listFilterTreeInfo: Object,
  defaultValues: Object
) => {
  let translations = _.get(labelTranslations, locale, {});
  if (_.isEmpty(translations)) {
    translations = defaultValues;
  }
  const filterOptionTranslationsKeys = Object.keys(translations).filter(key =>
    key.startsWith('filterOption|')
  );
  const filterOptionDisplayFields = [];
  _.forEach(filterOptionTranslationsKeys, (key) => {
    const optionName = translations[key];
    const [, filterId, filterOptionId] = key.split('|');
    const filterInfo = _.find(listFilterTreeInfo, { filterId });
    const filterOption = _.find(filterInfo?.filterOptions || [], { filterOptionId });
    filterOptionDisplayFields.push({
      name: key,
      label: `${filterOption?.label} (${filterInfo?.name})`,
      placeholder: optionName,
      multiline: false
    });
  });
  result[FILTER_OPTIONS_TAB] = filterOptionDisplayFields;
};

/**
 * Build list of sort by options label
 *
 * @param {object} sortByOptionDisplay
 * @param {object} i18n
 * @param {string} primaryLocale
 * @return {object}                   list of sort by options label
 */
export const getSortByOptionList = (sortByOptionDisplay: Object, i18n: Object) => {
  let sortByDisplay = { ...sortByOptionDisplay };

  if (_.isEmpty(sortByOptionDisplay)) {
    // Set default list
    sortByDisplay = { ...SORT_BY_OPTION_DISPLAY_DEFAULT };
  }

  // Return list from sort by option display
  // Replace token in string label display
  const returnValue = _.map(sortByDisplay, (value, key) => {
    let label = value;
    const matchingFound = label.match(/{{[^{}]*}}/g);
    if (!_.isEmpty(matchingFound[0])) {
      // Replace sort type
      const matchingValue = matchingFound[0];
      let sortTypeToken = _.trim(matchingValue, '{}');
      if (_.includes(sortTypeToken, 'metafield')) {
        label = _.replace(label, matchingValue, _.upperFirst(sortTypeToken));
        sortTypeToken = 'metafield';
      } else {
        const sortTypeLabel = SORT_TYPES_LABEL[sortTypeToken];
        label = _.replace(
          label,
          matchingValue,
          !_.isEmpty(sortTypeLabel) ? i18n.translate(sortTypeLabel) : ''
        );
      }
      if (!_.isEmpty(matchingFound[1])) {
        // Replace order
        const matchingValue = matchingFound[1];
        const sortOrderToken = _.trim(matchingValue, '{}');
        const sortTypeOrders = SORT_TYPES_ORDER[sortTypeToken] || [];
        const sortTypeOrder = _.find(sortTypeOrders, item => item.value === sortOrderToken, {});
        label = _.replace(
          label,
          matchingValue,
          !_.isEmpty(sortTypeOrder) ? i18n.translate(sortTypeOrder.label) : ''
        );
      }
    }
    return { name: key, label };
  });

  return returnValue;
};

/**
 * Build list of sort by options label
 *
 * @param {object} defaultValues
 * @param {object} sortByOptions
 * @param {Array<any>} recommendationFields raw fields of recommendation widget
 * @return {object}                   list of sort by options label
 */
export const buildTranslationFields = (
  i18n: Object,
  filterSettings: Object,
  dynamicFields: Object,
  defaultValues: Object,
  themeSetupV3: boolean,
  locale: string,
  listFilterTreeInfo: Object,
  isEnableShopifyIntegration: boolean
) => {
  const { sortByOptions = [], recommendationFields = [] } = dynamicFields;
  const { labelTranslations = {} } = filterSettings;
  const fields = _.cloneDeep(themeSetupV3 ? ITEMS_V2 : ITEMS_V1);

  // Build Sort by option fields base on sortByOptions
  const sortByOptionFields = _.map(sortByOptions, option => {
    const { name, label } = option;
    const placeholder = _.get(defaultValues, name, '');

    // Based on theme setup v3 to indicate tab of field
    // if (themeSetupV3) return { tab: 'ProductTab', group: 'sortBy', name, label, placeholder };

    return { tab: 'SortByTab', name, label, placeholder };
  });
  fields.push(...sortByOptionFields);

  // Hide field by settings, make sure execute this step before grouping fields by `tab`
  hideBySettings(fields, filterSettings);

  const groupItems = _.groupBy(fields, 'tab');
  for (const tabName in groupItems) {
    groupItems[tabName] = groupItems[tabName].map(item => {
      let label = item.label;
      if (_.isEmpty(item)) return null;
      const placeholder = _.get(defaultValues, item.name, '');
      if (!label) label = i18n.translate(`AdminHTML.Translation.${tabName}.${item.name}`);
      return _.assign({ label, placeholder }, item);
    });
  }

  buildRecommendationFields(groupItems, recommendationFields, i18n);

  if (isEnableShopifyIntegration) {
    buildFilterOptionFields(groupItems, labelTranslations, locale, listFilterTreeInfo, defaultValues);
  }
  return groupItems;
};

/**
 * Hide some field by filter settings
 * @param {*} groupItems
 * @param {*} filterSettings
 */
const hideBySettings = (fields: Object, filterSettings: Object) => {
  // Search setting
  const searchPanel = _.get(filterSettings, 'search.searchPanelBlocks', {});
  const pageEnabled = searchPanel?.pages?.active;
  const collectionEnabled = searchPanel?.collections?.active;
  const searchTipEnabled = searchPanel?.searchTips?.active;
  const searchTermSuggestionEnabled = searchPanel?.searchTermSuggestions?.active;
  const mostPopularProductEnabled = searchPanel?.mostPopularProducts?.active;

  if (!pageEnabled) {
    _.remove(fields, { name: 'searchPanelPage' });
  }
  if (!collectionEnabled) {
    _.remove(fields, { name: 'searchPanelCollection' });
  }
  if (!searchTipEnabled) {
    _.remove(fields, { name: 'searchTipsTitle' });
    _.remove(fields, { name: 'searchTipsContent' });
  }
  if (!searchTermSuggestionEnabled) {
    _.remove(fields, { name: 'noSearchResultSearchTermLabel' });
  }
  if (!mostPopularProductEnabled) {
    _.remove(fields, { name: 'noSearchResultProductsLabel' });
  }

  // Instant search setting
  const searchBoxOnClick = _.get(filterSettings, 'search.searchBoxOnclick', {});
  const productSuggestion = searchBoxOnClick?.productSuggestion?.status;
  const recentSearch = searchBoxOnClick?.recentSearch?.status ?? true;
  const termSuggestion = searchBoxOnClick?.searchTermSuggestion?.status;

  if (!productSuggestion) {
    _.remove(fields, { name: 'searchBoxOnclickProductsLabel' });
  }
  if (!recentSearch) {
    _.remove(fields, { name: 'searchBoxOnclickRecentSearchLabel' });
  }
  if (!termSuggestion) {
    _.remove(fields, { name: 'searchBoxOnclickSearchTermLabel' });
  }
};
